import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";

import type { AccountConfig } from "../api/hooks";
import { useDiscoverImapFolders } from "../api/hooks";
import { extractApiError } from "../api/client";

const wizardSteps = ["Credenziali", "Server", "Cartelle"];
const REDACTED_SECRET_PLACEHOLDER = "***REDACTED***";

function normalizeFolders(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function makeInitialState(initialAccount: AccountConfig) {
  return {
    username: initialAccount.username ?? "",
    label: initialAccount.label ?? "",
    password: initialAccount.password ?? "",
    host: initialAccount.host ?? "",
    port: initialAccount.port ?? 993,
    folders: [...(initialAccount.folders ?? [])]
  } satisfies AccountConfig;
}

interface BackupWizardDialogProps {
  open: boolean;
  initialAccount: AccountConfig;
  onClose: () => void;
  onComplete: (account: AccountConfig, sync?: { fullSyncFrom?: string }) => void;
}

export function BackupWizardDialog({ open, initialAccount, onClose, onComplete }: BackupWizardDialogProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState<AccountConfig>(() => makeInitialState(initialAccount));
  const [foldersInput, setFoldersInput] = useState(() => state.folders.join("\n"));
  const [showErrors, setShowErrors] = useState(false);
  const [discoveredFolders, setDiscoveredFolders] = useState<string[]>([]);
  const [fullSyncEnabled, setFullSyncEnabled] = useState(false);
  const [fullSyncFrom, setFullSyncFrom] = useState<string>("");

  const discoverFolders = useDiscoverImapFolders();

  useEffect(() => {
    if (open) {
      const snapshot = makeInitialState(initialAccount);
      setState(snapshot);
      setFoldersInput(snapshot.folders.join("\n") || "INBOX");
      setDiscoveredFolders(snapshot.folders ?? []);
      setFullSyncEnabled(false);
      setFullSyncFrom("");
      setActiveStep(0);
      setShowErrors(false);
    }
  }, [initialAccount, open]);

  const foldersList = useMemo(() => normalizeFolders(foldersInput), [foldersInput]);

  const isCredentialsValid = useMemo(() => {
    return state.username.trim().length > 0 && state.password.trim().length > 0;
  }, [state.password, state.username]);

  const isServerValid = useMemo(() => {
    return state.host.trim().length > 0 && Number.isFinite(state.port) && (state.port ?? 0) > 0;
  }, [state.host, state.port]);

  const isFoldersValid = foldersList.length > 0;
  const isMaskedPassword = state.password.trim() === REDACTED_SECRET_PLACEHOLDER;

  const nextDisabled = [isCredentialsValid, isServerValid, isFoldersValid][activeStep] === false;
  const nextLabel = activeStep === wizardSteps.length - 1 ? "Conferma" : "Avanti";

  const handleCancel = () => {
    onClose();
    setShowErrors(false);
  };

  const handleNext = () => {
    if (nextDisabled) {
      setShowErrors(true);
      return;
    }
    if (activeStep === wizardSteps.length - 1) {
      const trimmedLabel = state.label?.trim() ?? "";
      const account: AccountConfig = {
        ...state,
        username: state.username.trim(),
        host: state.host.trim(),
        port: state.port ?? 993,
        folders: foldersList.length > 0 ? foldersList : ["INBOX"]
      };
      if (trimmedLabel.length > 0) {
        account.label = trimmedLabel;
      } else {
        delete account.label;
      }
      const sync = fullSyncEnabled ? { fullSyncFrom: fullSyncFrom || undefined } : undefined;
      onComplete(account, sync);
      return;
    }
    setActiveStep((current) => current + 1);
  };

  const handleBack = () => {
    setShowErrors(false);
    setActiveStep((current) => Math.max(0, current - 1));
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Configurazione guidata casella PEC</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {wizardSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Credenziali di accesso</Typography>
            <TextField
              label="Nome visualizzato"
              value={state.label ?? ""}
              onChange={(event) => setState((prev) => ({ ...prev, label: event.target.value }))}
              helperText="Comparirà nelle schermate di selezione casella"
              fullWidth
            />
            <TextField
              label="Indirizzo PEC"
              value={state.username}
              onChange={(event) => setState((prev) => ({ ...prev, username: event.target.value }))}
              error={showErrors && state.username.trim().length === 0}
              helperText={showErrors && state.username.trim().length === 0 ? "Inserire l'indirizzo PEC completo." : ""}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password PEC"
              type="password"
              value={state.password}
              onChange={(event) => setState((prev) => ({ ...prev, password: event.target.value }))}
              error={showErrors && state.password.trim().length === 0}
              helperText={showErrors && state.password.trim().length === 0 ? "Inserire la password della casella." : ""}
              fullWidth
            />
          </Stack>
        ) : null}

        {activeStep === 1 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Server IMAP</Typography>
            <TextField
              label="Server IMAP"
              value={state.host}
              onChange={(event) => setState((prev) => ({ ...prev, host: event.target.value }))}
              error={showErrors && state.host.trim().length === 0}
              helperText={showErrors && state.host.trim().length === 0 ? "Indicare l'host IMAP fornito dal provider." : ""}
              fullWidth
            />
            <TextField
              label="Porta IMAP"
              type="number"
              value={state.port ?? 993}
              onChange={(event) => {
                const numeric = Number(event.target.value);
                setState((prev) => ({ ...prev, port: Number.isFinite(numeric) ? numeric : 993 }));
              }}
              error={showErrors && (!Number.isFinite(state.port) || (state.port ?? 0) <= 0)}
              helperText={showErrors && (!Number.isFinite(state.port) || (state.port ?? 0) <= 0) ? "Inserire una porta valida." : ""}
              fullWidth
            />
          </Stack>
        ) : null}

        {activeStep === 2 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1">Cartelle da monitorare</Typography>
            <Stack spacing={1} direction="row" alignItems="center">
              <Button
                variant="outlined"
                size="small"
                disabled={
                  !state.username.trim() ||
                  !state.password.trim() ||
                  isMaskedPassword ||
                  !state.host.trim() ||
                  discoverFolders.isPending
                }
                onClick={() => {
                  setShowErrors(false);
                  if (isMaskedPassword) {
                    return;
                  }
                  discoverFolders.mutate(
                    {
                      host: state.host.trim(),
                      port: state.port ?? 993,
                      username: state.username.trim(),
                      password: state.password
                    },
                    {
                      onSuccess: (folders) => {
                        setDiscoveredFolders(folders);
                        // Non pre-selezionare automaticamente le cartelle
                      }
                    }
                  );
                }}
              >
                {discoverFolders.isPending ? "Caricamento..." : "Scopri cartelle IMAP"}
              </Button>
              {discoverFolders.isError ? (
                <Typography variant="body2" color="error">
                  {extractApiError(discoverFolders.error)}
                </Typography>
              ) : null}
              {isMaskedPassword ? (
                <Typography variant="body2" color="warning.main">
                  Inserisci la password reale della casella per usare la scoperta cartelle IMAP.
                </Typography>
              ) : null}
            </Stack>

            {discoveredFolders.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Seleziona le cartelle da includere:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {discoveredFolders.map((folder) => {
                    const active = foldersList.includes(folder);
                    return (
                      <Button
                        key={folder}
                        variant={active ? "contained" : "outlined"}
                        size="small"
                        onClick={() => {
                          if (active) {
                            setFoldersInput(foldersList.filter((f) => f !== folder).join("\n"));
                          } else {
                            setFoldersInput([...foldersList, folder].join("\n"));
                          }
                        }}
                      >
                        {folder}
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            )}

            <TextField
              label="Cartelle da includere"
              value={foldersInput}
              onChange={(event) => setFoldersInput(event.target.value)}
              error={showErrors && foldersList.length === 0}
              helperText={
                showErrors && foldersList.length === 0
                  ? "Indicare almeno una cartella (es. INBOX)."
                  : "Separare con virgola o nuova riga"
              }
              multiline
              minRows={3}
              fullWidth
            />
            <Box sx={{ color: "text.secondary" }}>
              <Typography variant="body2">
                Verrà creata una configurazione per {state.username || "la casella"} con {foldersList.length || 0} cartelle monitorate.
              </Typography>
            </Box>

            <Stack spacing={1}>
              <Typography variant="subtitle2">Scarica storico</Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant={fullSyncEnabled ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setFullSyncEnabled((prev) => !prev)}
                  >
                    {fullSyncEnabled ? "Storico attivo" : "Storico disattivato"}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Esegui download completo a partire da una data.
                  </Typography>
                </Stack>
                <TextField
                  label="Dal"
                  type="date"
                  size="small"
                  value={fullSyncFrom}
                  disabled={!fullSyncEnabled}
                  onChange={(event) => setFullSyncFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Data inizio storico (lascia vuoto per tutto)"
                />
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Annulla</Button>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Indietro
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={nextDisabled}>
          {nextLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
